import {GetMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/browse_page/v1/meta/service.types';
import {Head} from '@aboutyou/head-elements';
import {TrackContentPageView} from '@aboutyou/tracking';
import {doAsync} from '@aboutyou/utils';
import React, {memo, useEffect} from 'react';
import {
  extractMetaAndLinkTags,
  isSeoData,
  setDataTestIds,
} from '../../../shared-deprecated/metaUtils';
import {useUpdateTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';

export interface MetaViewProps {
  removeGenderQueryParam?: boolean;
}

export const MetaView: React.FC<GetMetaDataResponse & MetaViewProps> = memo(
  function MetaView({meta, recommendedTargetGroup, removeGenderQueryParam}) {
    const {isTargetGroupUpdating} = useUpdateTargetGroup(
      recommendedTargetGroup,
    );

    // Removes the gender query parameter from the URL to avoid having two sources of truth for the gender (URL and cookie).
    // The gender query parameter only serves as a trigger to change the gender.
    // This is needed for the case when the gender is changed from the offCanvas, not to end up in an inconsistent state,
    // where the offCanvas shows the selected gender based on the cookie, and the Browse page based on the URL.
    // eslint-disable-next-line local-rules/too-complex-useeffects
    useEffect(() => {
      if (!removeGenderQueryParam) {
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has('gender')) {
        searchParams.delete('gender');

        const searchParamsString = searchParams.toString();

        doAsync(() => {
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${
              searchParamsString ? `?${searchParamsString}` : ''
            }`,
          );
        });
      }
    }, [recommendedTargetGroup, removeGenderQueryParam]);

    if (!isSeoData(meta)) {
      return null;
    }

    const seoData = meta.type.seoData;
    const [metaTags, linkTags] = setDataTestIds(
      extractMetaAndLinkTags(seoData.elements),
    );

    return (
      <>
        {!isTargetGroupUpdating && (
          <TrackContentPageView pageTitle={seoData.title} />
        )}
        <Head meta={metaTags} link={linkTags}>
          <title data-testid="MetaTitle">{seoData.title}</title>
        </Head>
      </>
    );
  },
);
